/**
 * The organisation's people, one card each: who they are, their access and
 * where it applies, and one menu holding every change -- the access itself
 * (`ChangeRoleDialog`), address reading (`GrantAddressReader`) and removal.
 *
 * Nothing here decides who may change a role or remove a member; the API
 * does, and this only renders what it answers (§3). The one rule enforced
 * client-side is that the access dialog never commits ADDRESS_READER: that
 * grant goes through `GrantAddressReader`'s own confirmed dialog (§4.7, §16).
 *
 * A null `campaign_id` is a tenant-wide grant, not "no campaign" -- rendering
 * it the same as a scoped row would hide the exact distinction §5.1 draws.
 *
 * `MembershipRow` carries no name field, so the email is the identity and the
 * avatar's initials come from its local part.
 */
import { Ellipsis } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { ConfirmDialog, FormDialog } from '../../../chrome/form';
import { IconHint } from '../../../chrome/hint';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { useAuthState } from '../../session/authState';
import { Alert, AlertTitle } from '../../../ui/alert';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import type { MembershipRow } from '../api/schemas';
import { tt } from '../i18n';
import { tm } from '../pages/team.messages';
import { GrantAddressReader } from './GrantAddressReader';
import { RolePicker } from './RolePicker';
import { useChangeRole, useMembers, useRevokeMembership } from './useMembers';

const MEMBERS_HEADING_ID = 'members-heading';

function scopeOf(member: MembershipRow): string {
  return member.campaign_id === null
    ? tt('members.scope.tenantWide')
    : (member.campaign_name ?? member.campaign_id);
}

function initialsOf(email: string | null): string {
  const local = email?.split('@')[0];
  return local === undefined || local === '' ? '?' : local.slice(0, 2).toUpperCase();
}

type Action = { kind: 'role' | 'remove' | 'grant'; member: MembershipRow };

function ChangeRoleDialog(props: {
  tenantId: string;
  member: MembershipRow;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const changeRole = useChangeRole(props.tenantId);
  const [role, setRole] = useState<string>(props.member.role);
  // The one defined 409 on this route: a tenant keeps at least one
  // tenant-wide administrator. The client names that refusal; it never counts
  // admins or predicts it.
  const lastAdmin = changeRole.error?.failure.kind === 'conflict';
  const email = props.member.email ?? tt('members.noEmail');
  return (
    <FormDialog
      open
      onOpenChange={props.onOpenChange}
      title={tm('role.title', { email })}
      submitLabel={tm('role.save')}
      pending={changeRole.isPending}
      submitDisabled={role === props.member.role}
      failure={lastAdmin ? null : (changeRole.error ?? null)}
      onSubmit={(event) => {
        event.preventDefault();
        return changeRole.mutateAsync({ membershipId: props.member.id, role });
      }}
    >
      {lastAdmin && (
        <Alert variant="destructive">
          <AlertTitle>{tt('members.last_admin')}</AlertTitle>
        </Alert>
      )}
      <RolePicker
        purpose="assign"
        value={role}
        className="w-full"
        onChange={(next) => {
          // ADDRESS_READER is granted only through GrantAddressReader.
          if (next !== 'ADDRESS_READER') setRole(next);
        }}
      />
    </FormDialog>
  );
}

function MemberCard(props: {
  member: MembershipRow;
  isSelf: boolean;
  onAction: (kind: Action['kind']) => void;
}): ReactNode {
  const { member } = props;
  return (
    <Card className="h-full gap-3 py-4">
      <CardHeader className="flex flex-row items-center gap-3 px-4">
        <Avatar>
          <AvatarFallback aria-hidden>{initialsOf(member.email)}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {member.email === null ? (
            <span className="truncate text-muted-foreground">{tt('members.noEmail')}</span>
          ) : (
            <span className="truncate font-medium" title={member.email}>
              {member.email}
            </span>
          )}
          {props.isSelf && <Badge variant="secondary">{tm('members.you')}</Badge>}
        </div>
        <DropdownMenu>
          <IconHint label={tm('member.actions')}>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon">
                <Ellipsis aria-hidden />
              </Button>
            </DropdownMenuTrigger>
          </IconHint>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => props.onAction('role')}>
              {tm('member.changeRole')}
            </DropdownMenuItem>
            {member.role !== 'ADDRESS_READER' && (
              <DropdownMenuItem onSelect={() => props.onAction('grant')}>
                {tm('member.grantAddress')}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => props.onAction('remove')}>
              {tm('member.remove')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2 px-4">
        <Badge variant="secondary">{tt(`role.short.${member.role}`)}</Badge>
        <Badge variant="outline">{scopeOf(member)}</Badge>
      </CardContent>
    </Card>
  );
}

export function MemberList(props: { tenantId: string }): ReactNode {
  const { tenantId } = props;
  const members = useMembers(tenantId);
  const revoke = useRevokeMembership(tenantId);
  const selfId = useAuthState().user?.uid ?? null;
  const [action, setAction] = useState<Action | null>(null);

  function closeAction(open: boolean): void {
    if (!open) setAction(null);
  }

  return (
    <section aria-labelledby={MEMBERS_HEADING_ID} className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 id={MEMBERS_HEADING_ID} className="text-title-md">
          {tt('members.heading')}
        </h2>
        {members.data !== undefined && <Badge variant="secondary">{members.data.length}</Badge>}
      </div>
      <QueryStates query={members} empty={<EmptyState title={tm('members.empty')} />}>
        {(data) => (
          <ul
            aria-labelledby={MEMBERS_HEADING_ID}
            className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
          >
            {data.map((member) => (
              <li key={member.id}>
                <MemberCard
                  member={member}
                  isSelf={member.user_id === selfId}
                  onAction={(kind) => setAction({ kind, member })}
                />
              </li>
            ))}
          </ul>
        )}
      </QueryStates>
      {action?.kind === 'role' && (
        <ChangeRoleDialog tenantId={tenantId} member={action.member} onOpenChange={closeAction} />
      )}
      {action?.kind === 'remove' && (
        <ConfirmDialog
          open
          onOpenChange={closeAction}
          title={tm('remove.title', { email: action.member.email ?? tt('members.noEmail') })}
          description={tt('members.remove.confirm', {
            email: action.member.email ?? tt('members.noEmail'),
          })}
          confirmLabel={tm('remove.confirm')}
          destructive
          pending={revoke.isPending}
          failure={revoke.error?.failure ?? null}
          onConfirm={() => revoke.mutateAsync({ membershipId: action.member.id })}
        />
      )}
      {action?.kind === 'grant' && (
        <GrantAddressReader
          tenantId={tenantId}
          membership={action.member}
          open
          onOpenChange={closeAction}
        />
      )}
    </section>
  );
}
